import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../shared-components/custom-toaster/toaster.service';
import { TranslateService } from '../../services/translate/translate.service';
import { LogService } from '../../services/log/log.service';
import { ServerApiService } from '../../server-api/server-api.service';
import { OperationResult } from '../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { AppContextService } from '../../services/app-context.service';
import { CustomInputComponent } from '../../shared-components/custom-input/custom-input.component';
import { finalize } from 'rxjs/operators';
import { ValidationRule } from '../../models/validation-rules/validation-rules';

@Component({
	selector: 'app-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss'],
	standalone: false,
})
export class ResetPasswordComponent implements OnInit {
	@ViewChild('password1Input') password1Input!: CustomInputComponent;
	@ViewChild('password2Input') password2Input!: CustomInputComponent;

	public isPerformingRequest = false;
	public processCompleted = false;
	public showRules = false;

	public password1: string;
	public password2: string;
	public isSubmitting: boolean = false;

	public password1Valid: boolean = false;
	public password2Valid: boolean = false;
	public isFormValid: boolean = false;

	private resetToken: string;
	private tokenId: string;
	private username: string;

	public userLanguage: string;

	constructor(
		private serverApiService: ServerApiService,
		private toasterService: ToasterService,
		private translateService: TranslateService,
		private logger: LogService,
		private route: ActivatedRoute,
		private appContext: AppContextService,
		private router: Router
	) {
		this.route.queryParams.subscribe((params) => {
			let base64Token = params['token'];

			if (!base64Token) {
				this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_TOKEN_FROM_ROUTE', 'list-errorCodes'));
			}

			let token = atob(base64Token);

			if (!!token) {
				let parameters: string[] = token.split('&');

				if (parameters.length < 3)
					this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_TOKEN_CONTENT', 'list-errorCodes'));
				else {
					this.tokenId = parameters[0].split(':')[1];
					this.resetToken = parameters[1].split(':')[1];
					this.username = parameters[2].split(':')[1];

					if (!this.resetToken || !this.username || !this.tokenId) {
						this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_TOKEN_PARAMETERS', 'list-errorCodes'));
					}
				}
			}
		});
	}

	ngOnInit() {
		this.getUserLanguage();
	}

	public resetPassword(): void {
		this.isSubmitting = true;
		this.validateAllFields();

		if (!this.isFormValid) {
			this.isSubmitting = false;
			return;
		}

		this.logger.log('isPerformingRequest: ' + this.isPerformingRequest);
		this.logger.log('Reseting Password for user ' + this.username + ' with token ' + this.resetToken);

		if (!this.username) {
			const msgNoUsername = this.translateService.translateByLanguage('resetPassword.emptyUsername', this.userLanguage);
			this.toasterService.showError(msgNoUsername);
			this.isSubmitting = false;
			return;
		}

		if (!this.password1 || !this.password2) {
			const msgNoPassword = this.translateService.translateByLanguage('resetPassword.emptyPassword', this.userLanguage);
			this.toasterService.showError(msgNoPassword);
			this.isSubmitting = false;
			return;
		}

		if (this.password1 !== this.password2) {
			const msgdiffenretPassword = this.translateService.translateByLanguage('resetPassword.passwordsDontMatch', this.userLanguage);
			this.toasterService.showError(msgdiffenretPassword);
			this.isSubmitting = false;
			return;
		}

		if (!this.resetToken) {
			const msgNoToken = this.translateService.translateByLanguage('resetPassword.emptyToken', this.userLanguage);
			this.toasterService.showError(msgNoToken);
			this.isSubmitting = false;
			return;
		}

		const serviceCall = this.serverApiService.resetUserPassword(this.username, this.password1, this.resetToken, this.tokenId);
		this.logger.log('Recovering password for token' + this.resetToken);
		this.isPerformingRequest = true;
		serviceCall
			.pipe(
				finalize(() => {
					this.isSubmitting = false;
				})
			)
			.subscribe({
				next: (operationResult: OperationResult<boolean>) => {
					this.processResetPasswordResponse(operationResult);
					this.isPerformingRequest = false;
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.processResetPasswordCallError(httpErrorResponse);
					this.logger.log(httpErrorResponse.message);
					this.logger.warn('warning: ' + httpErrorResponse.message);
					this.isPerformingRequest = false;
					return false;
				},
			});

		return;
	}
	processResetPasswordCallError(httpError: HttpErrorResponse): void {
		this.toasterService.showError(httpError.message);
	}
	processResetPasswordResponse(operationResult: OperationResult<boolean>): void {
		if (operationResult.IsValid) {
			const msgResetSuccess = this.translateService.translateByLanguage('resetPassword.successful', this.userLanguage);
			this.toasterService.showSuccess(msgResetSuccess);
			this.processCompleted = true;
		} else {
			const msgNoResetPassword = this.translateService.translateByLanguage('resetPassword.error', this.userLanguage);
			this.toasterService.showWarning(msgNoResetPassword);
		}
	}

	private getUserLanguage() {
		this.userLanguage = this.appContext.getCurrentLanguage();

		const getUserLangageCodeCall = this.serverApiService.getUserLanguageCode(this.username);
		getUserLangageCodeCall.subscribe({
			next: (operationResult: OperationResult<string>) => {
				this.processGetUserLangageCodeCallResponse(operationResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.processGetUserLangageCodeCallError(httpErrorResponse);
				this.logger.log(httpErrorResponse.message);
				this.logger.warn('warning: ' + httpErrorResponse.message);
				return false;
			},
		});
	}

	processGetUserLangageCodeCallError(httpError: HttpErrorResponse): void {
		this.toasterService.showError(httpError.message);
	}
	processGetUserLangageCodeCallResponse(operationResult: OperationResult<string>): void {
		if (operationResult.IsValid) {
			this.userLanguage = operationResult.Data;
		}
	}

	onPassword1Change(value: string): void {
		this.password1 = value;
	}

	onPassword2Change(value: string): void {
		this.password2 = value;
	}

	onPassword1ValidationChange(validation: { isValid: boolean; errors: string[] }): void {
		this.password1Valid = validation.isValid;
		this.updateFormValidity();
	}

	onPassword2ValidationChange(validation: { isValid: boolean; errors: string[] }): void {
		this.password2Valid = validation.isValid;
		this.updateFormValidity();
	}

	private updateFormValidity(): void {
		const hasValidationErrors = !this.password1Valid || !this.password2Valid;
		this.isFormValid = !hasValidationErrors;
	}

	private validateAllFields(): void {
		this.password1Input.validate();
		this.password2Input.validate();
	}

	public navigateToLogin(): void {
		this.router.navigate(['/public/login']);
	}

	private get inputRules() {
		return CustomInputComponent.createRules(this.translateService);
	}

	get passwordValidationRules(): ValidationRule[] {
		return [
			this.inputRules.minLength(8),
			this.inputRules.custom(
				(value) => /\d/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule2', this.userLanguage),
				'has-digit'
			),
			this.inputRules.custom(
				(value) => /[A-Z]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule3', this.userLanguage),
				'has-uppercase'
			),
			this.inputRules.custom(
				(value) => /[a-z]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule4', this.userLanguage),
				'has-lowercase'
			),
			this.inputRules.custom(
				(value) => /[!@#$%^&*?_~-£()]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule5', this.userLanguage),
				'has-special-char'
			),
		];
	}
}
